import {
  AiFeature,
  AIDecision,
  AutomationAction,
  CalculationType,
  ClaimResolutionLineType,
  ClaimType,
  SenderType,
  type ClaimAutomation,
} from './database/claims-svc';
import { AIProvider } from './ai-provider';
// ClaimAutomationStatus is now a Prisma enum (durable run status on the row),
// re-exported here so existing `@monolog/shared/types` imports keep working and
// pick up the new SUPERSEDED/STOPPED states. Same pattern as AutomationEventSource.
export { AutomationEventSource, ClaimAutomationStatus } from './database/claims-svc';
import { Capability, Store, ClaimAutomationSettings, ClaimAutomationCapability } from './database/merchant-svc';

export enum YofiPredictionValue {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

/** A claim automation as the automation endpoints return it: the run plus what they derive from its ai_request calls. */
export type ClaimAutomationRunView = ClaimAutomation & {
  promptRequest?: object | null;
  model?: AIProvider | null;
  gatewayModel?: string | null;
  cost?: number | null;
  inputTokens?: number;
  outputTokens?: number;
  cacheCreationInputTokens?: number;
  cacheReadInputTokens?: number;
  iterations?: number | null;
};

export interface ClaimHistoryEvent {
  id?: string;
  timestamp?: string;
  eventType?: string;
  data?: {
    claimId?: string;
    claimType?: string;
    oldState?: string;
    newState?: string;
    oldClaimType?: string;
    newClaimType?: string;
    userName?: string;
    senderName?: string;
    senderType?: string;
    hasMedia?: boolean;
    mediaCount?: number;
    messageContent?: string;
    [key: string]: unknown;
  };
}

export interface ClaimAutomationItemResolution {
  id: string;
  name: string;
  sku: string | null;
  claimType: string;
  resolutionId: string | null;
  sourceItemId: string | null;
  resolutionFinal: string;
  sourceProductId: string | null;
  sourceVariantId: string | null;
  resolutionQuantity: number;
  resolutionRequested: string;
  resolutionFlatAmount: number | null;
  resolutionPercentage: number;
  /** Quote-backed runs only: the line total the pricing tool priced and execution pays, whatever its calculation type. */
  resolutionPricedAmount?: number | null;
  // Substitute ship-target; absent when the original item ships.
  substituteProductId?: string | null;
  substituteVariantId?: string | null;
  substituteSku?: string | null;
  substituteName?: string | null;
  substitutePrice?: number | null;
  substituteImageUrl?: string | null;
  selectedOptions?: { name: string; value: string }[] | null;
  // Set on a line that was auto-attached by the platform (not chosen by the AI) because it is the
  // linked personalization/customization line of a reshipped item — e.g. a separate embroidery SKU.
  // `linkedToClaimItemId` names the reshipped claim item it decorates; `properties` are its
  // order-item personalization attributes (embroidery text/color, build id, etc.).
  autoAddedPersonalization?: boolean;
  linkedToClaimItemId?: string | null;
  properties?: { name: string; value: string }[] | null;
  // Set on an order line the AI chose to resolve on top of what the customer filed (the automation
  // equivalent of the manual UI's "add item from order"). No claim item exists for it yet — its `id`
  // is the order-item id — so a consumer resolves the order line by `sourceItemId`. It is priced from
  // that order line, not reshipped at $0 like an auto-attached personalization line.
  aiAddedOrderItem?: boolean;
  aiAddedReason?: string | null;
}

export enum WaitForReason {
  CUSTOMER_REPLY = 'customer_reply',
  BRAND_REPLY = 'brand_reply',
  FRAUD_UPDATE = 'fraud_update',
  FULFILLMENT_UPDATE = 'fulfillment_update',
  OTHER = 'other',
}

/**
 * Identifies the caller/cause of a claim state change so downstream
 * listeners (e.g. the STATE_CHANGE automation trigger) can disambiguate
 * human-initiated transitions from system-initiated ones.
 */
export enum ClaimStateChangeSource {
  AUTOMATION_WAIT = 'AUTOMATION_WAIT',
  AUTOMATION_EXECUTION_FAILURE = 'AUTOMATION_EXECUTION_FAILURE',
  AUTOMATION_ESCALATION = 'AUTOMATION_ESCALATION',
  /** A support agent saved an internal note and moved the claim to On Hold for the brand. */
  MANUAL_HOLD_FOR_BRAND = 'MANUAL_HOLD_FOR_BRAND',
  /** The transition `updateStateBasedOnMessage` makes in response to a message, whoever sent it. */
  MESSAGE = 'MESSAGE',
}

/** Outcome of an agent handing a takeover back to the AI early. */
export enum HandBackStatus {
  /** The takeover ended; `automationId` is null when the claim isn't eligible for a run and needs a human. */
  HANDED_BACK = 'handed_back',
  /** The claim was already settled, so the pause was dropped and no run started. */
  REAPED = 'reaped',
  /** Someone else ended or changed the pause first; nothing was done. */
  ALREADY_RELEASED = 'already_released',
}

export interface HandBackResult {
  claimId: string;
  status: HandBackStatus;
  runRequested?: boolean;
  automationId?: string | null;
}

export interface ProposedShippingAddress {
  firstName: string;
  lastName: string;
  company?: string | null;
  address1: string;
  address2?: string | null;
  city: string;
  state?: string | null;
  zip: string;
  country: string;
}

/**
 * A per-line explanation of how a resolution amount was derived. Persisted on the resolution
 * (and nested on the automation's actionObject, even when never executed) so a reviewer can see
 * WHY a total is what it is — instead of reverse-engineering a `resolutionFlatAmount` of `null`.
 */
export interface ResolutionAmountBreakdownLine {
  lineType: ClaimResolutionLineType;
  resolutionType: string;
  calculationType: CalculationType;
  description: string;
  /** Per-unit order pricing the calc draws from (null for non-item lines). */
  basis: { unitPrice: number; discount: number; tax: number; quantity: number; percentage: number } | null;
  /** Value at 100% (FLAT lines: at their own %, unless a 0% line pays out) — the baseline a buffer is measured against. */
  baseValue: number;
  /** The flat amount supplied by the caller for a FLAT line, else null. */
  flatOverride: number | null;
  /**
   * flatOverride − baseValue for FLAT lines; 0 when the flat amount is exactly the derivation at its percentage
   * (e.g. a priced quote line); null otherwise.
   */
  buffer: number | null;
  /** Final amount the customer receives for this line. */
  resolvedAmount: number;
  /** Amount OP reimburses the store for this line (post coverage matrix). */
  coveredAmount: number;
  creditStore: boolean;
  /** Human-readable derivation, e.g. "$24.99 × 1 @ 100% = $24.99" or "flat $21.99 (base $11.99, buffer +$10.00)". */
  formula: string;
}

export interface ResolutionAmountBreakdown {
  currency: string;
  /** Full resolved value to the customer across all lines. */
  totalAmount: number;
  /** What OP reimburses the store. */
  creditedAmount: number;
  /** Resolved value on lines that don't credit the store. */
  notCreditedAmount: number;
  lines: ResolutionAmountBreakdownLine[];
  /** One-line plain-English summary of the total derivation. */
  summary: string;
}

export type ClaimAutomationActionObject = {
  message?: string;
  claimItems?: ClaimAutomationItemResolution[];
  creditStore?: boolean;
  refundShipping?: boolean;
  resolvedExternally?: boolean;
  waitFor?: WaitForReason;
  /** Only on automations recorded before waits stopped scheduling an on-hold re-check. */
  waitUntil?: string;
  claimType?: ClaimType;
  shippingAddress?: ProposedShippingAddress;
  /** Dry-run breakdown of the suggested resolution amount; recorded even when never executed. */
  amountBreakdown?: ResolutionAmountBreakdown;
  /** Order changes applied when a resolve executes (e.g. a free-return tag). */
  orderUpdates?: ClaimAutomationOrderUpdates;
  /** On a JUDGEMENT escalation: the priced resolution the agent would make if a person approved it. */
  recommendation?: EscalationRecommendation;
} | null;

export interface ClaimAutomationOrderUpdates {
  tagsAdd: string[];
}

/** What approving an escalation's recommendation does: resolve the claim, or send the customer a message. */
export enum EscalationRecommendationAction {
  RESOLVE = 'RESOLVE',
  SEND_MESSAGE = 'SEND_MESSAGE',
}

export type EscalationRecommendation = {
  /** Absent on recommendations made before messages could be recommended, which all resolve. */
  action?: EscalationRecommendationAction;
  aiDecision: AIDecision | null;
  resolutionType: string | null;
  customerMessage?: string;
  claimItems?: ClaimAutomationItemResolution[];
  amountBreakdown?: ResolutionAmountBreakdown;
};

export enum RecommendationApprovalState {
  QUEUED = 'QUEUED',
  FAILED = 'FAILED',
}

/** The kinds of item the Control Panel lists, one tab each. Only escalations can be actioned from it so far. */
export enum ControlPanelItemType {
  ESCALATION = 'ESCALATION',
  TAKEOVER = 'TAKEOVER',
  SCHEDULED = 'SCHEDULED',
  FAILED = 'FAILED',
}

/** One item waiting on a person, as the Control Panel lists it. */
export type OpenRecommendation = {
  /** Unique across types: `<type>:<source row id>`. */
  id: string;
  type: ControlPanelItemType;
  /** The automation run behind the item; null for a takeover, which is a pause rather than a run. */
  automationId: string | null;
  claimId: string;
  orderId: string;
  /** The order number the store shows the customer, e.g. 5099. */
  orderNumber: string | null;
  storeId: string | null;
  claimType: string | null;
  /** Escalations only. */
  trigger: string | null;
  /** Escalations only: JUDGEMENT or CAPABILITY; null on escalations made before the kind was recorded. */
  kind: string | null;
  confidence: number | null;
  /** When the item started waiting: escalated, taken over, scheduled or failed. */
  since: string;
  /** Scheduled: when it will run. Takeover: when the watchdog nudges the owner. Escalation: its next reminder. */
  dueAt: string | null;
  /** Escalations only: when the watchdog last reminded the team about it; null if it hasn't yet. */
  lastNudgedAt?: string | null;
  /** Escalations only: when the watchdog's auto-approval failed, after which it is left for a person. */
  autoApproveFailedAt?: string | null;
  /** Escalations only: when it auto-approves if nobody acts first; null when it never will. */
  autoApproveAt?: string | null;
  message: string | null;
  /** One line describing a non-escalation item, e.g. the note left on a takeover or the error of a failed run. */
  summary: string | null;
  /** Takeovers only: the short reason shown as a badge, e.g. "Waiting on the brand" or "Stopped". */
  reasonLabel: string | null;
  recommendation: EscalationRecommendation | null;
  /** False with no recommendation, on a brand hold, or on any non-escalation type. */
  approvable: boolean;
  approval: { state: RecommendationApprovalState; reason?: string } | null;
};

/** A claim's open escalation, as the claims-table peek shows it. */
export type ClaimEscalationPeek = {
  automation: {
    id: string;
    claimId: string;
    actionObject: ClaimAutomationActionObject;
    /** Trimmed to the fraud level the recommendation card reads; null when the run recorded none. */
    confidenceScorecard: Pick<ConfidenceScorecard, 'fraudRiskLevel'> | null;
  };
  approvable: boolean;
  brandHold: boolean;
  escalatedAt: string;
  message: string | null;
  trigger: string | null;
};

/** The Control Panel list; `truncated` means the oldest escalations filled the cap and newer ones were left out. */
export type OpenRecommendationList = { items: OpenRecommendation[]; truncated: boolean };

export interface ReshipmentContext {
  sourceOrderId: string;
  sourceOrderNumber: string | null;
  sourceCreatedAt: Date;
  sourceCancelledAt: Date | null;
  orderItems: Array<{ productName: string; quantity: number; price: number; sku: string | null }>;
}

/**
 * Event types for claim lifecycle tracking in Redis streams
 */
export enum ClaimEventType {
  CLAIM_CREATED = 'CLAIM_CREATED',
  STATE_CHANGED = 'STATE_CHANGED',
  MESSAGE_SENT = 'MESSAGE_SENT',
  AUTOMATION_RUN = 'AUTOMATION_RUN',
  RESOLUTION_CREATED = 'RESOLUTION_CREATED',
  ESCALATED = 'ESCALATED',
  UNKNOWN_EVENT = 'UNKNOWN_EVENT',
}

/**
 * Automation settings returned by merchant-svc
 */
export type AutomationSettings = {
  threshold: number;
  claimValueThreshold?: { min: number | null; max: number | null } | null;
  aiBotName?: string | null;
  aiBotIcon?: string | null;
};

/**
 * Context data for a store that rarely changes - cached in memory with TTL
 */
export interface StoreAutomationContext {
  capabilities: Capability;
  automationSettings: (ClaimAutomationSettings & { automationCapabilities: ClaimAutomationCapability | null }) | null;
  store: Store;
  sops: StoreSop[];
  brandConfig: BrandConfig;
}

/**
 * Store SOP (Standard Operating Procedure) from merchant-svc
 */
export interface StoreSop {
  id: string;
  title: string;
  content: string;
}

/**
 * Store Macro from merchant-svc
 */
export interface StoreMacro {
  id: string;
  folder: string;
  title: string;
  content: string;
}

/**
 * Complete brand configuration for a store
 * Uses raw markdown content for flexibility - the AI interprets it directly
 */
export interface BrandConfig {
  global: string; // Raw GLOBAL.md content
  soul: string; // Raw SOUL.md content (agent personality)
  agent: string; // Raw AGENT.md content (directive overrides)
  sops: StoreSop[]; // Active SOPs from merchant-svc
  macros?: StoreMacro[]; // Active macros from merchant-svc (optional)
}

/**
 * Payload interface for process_claim_automation event
 */
export interface ProcessClaimAutomationPayload {
  claimId: string;
  orderId: string;
  fraudReportId?: string;
  eventSource?: string;
  initiatedAt?: number;
  storeSlug?: string;
  /**
   * When true, skip the auto-execute gate even if all settings/thresholds would
   * normally allow it. Used by the merchant-facing "Ask AI (manual)" flow so
   * the agent produces a suggestion that always requires human approval.
   */
  manualOnly?: boolean;
}

export interface TrainingSuggestion {
  file: string;
  rationale: string;
  originalContent: string;
  proposedContent: string;
}

/**
 * Stored in ClaimAutomation.pendingToolCall — captures the agent's write tool call
 * for later execution (human approval) or immediate execution (auto-execute).
 */
export interface PendingToolCall {
  toolName: string;
  input: Record<string, unknown>;
}

/**
 * Overrides the human can apply when executing a pending tool call.
 */
export interface ExecuteOverrides {
  skipMessage?: boolean;
  customerMessage?: string;
}

/**
 * Stored in ClaimAutomation.actionObject when a customer message is deferred
 * until an async dependency (e.g. reship order creation) completes.
 */
export interface DeferredMessage {
  messageContent: string;
  senderName: string;
  // Optional: payloads queued before this field existed fall back to `automatic` at send time.
  senderType?: SenderType;
  senderId: string;
  storeId: string;
}

/**
 * Configuration for the confidence scorecard, loaded from CONFIDENCE-SCORECARD.md.
 * Two-dimension model: Data Readiness + Decision Clarity.
 */
export interface ScorecardConfig {
  dataQualityPoints: Record<'sufficient' | 'partial' | 'insufficient' | 'not_called', number>;
  dimensionWeights: { dataReadiness: number; decisionClarity: number; llmConfidence: number };
  decisionClarityPenalties: {
    noComplianceReport: number;
    highIterations: number;
    veryHighIterations: number;
    highIterationThreshold: number;
    veryHighIterationThreshold: number;
  };
  guardrails: {
    minLlmConfidence: number;
    requireDenyPrecedent: boolean;
  };
}

/**
 * Confidence scorecard with 3 dimensions + guardrails for auto-execution gating.
 */
export interface ConfidenceScorecard {
  finalScore: number; // 0-100, overwrites ClaimAutomation.confidence

  guardrails: {
    triggered: boolean;
    reasons: string[];
  };

  dataReadiness: {
    score: number;
    weight: number;
    requiredTools: string[];
    tools: Record<string, { status: 'sufficient' | 'partial' | 'insufficient' | 'not_called'; score: number }>;
  };

  decisionClarity: {
    score: number;
    weight: number;
    penalties: Array<{ reason: string; points: number }>;
  };

  llmConfidence: {
    score: number;
    weight: number;
  };

  fraudRiskLevel: FraudRiskLevel;
  llmSelfReported: number | null;

  /** Null when no tool failed; absent on rows scored before this was recorded. */
  toolFailureCap?: ToolFailureCap | null;
}

/** A tool call whose last result was an error, with a trimmed error excerpt. */
export interface ScorecardFailedToolCall {
  toolName: string;
  error: string;
}

/** Why a scorecard's finalScore was held at the tool-failure cap. */
export interface ToolFailureCap {
  cap: number;
  /** Score before the cap; the cap only lowers it. */
  uncappedScore: number;
  failedToolCalls: ScorecardFailedToolCall[];
}

export enum FraudRiskLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  UNKNOWN = 'unknown',
}

/** Marker on a tool_result the write-payload validation gate produced instead of capturing a write. */
export const INVALID_TOOL_INPUT_CODE = 'INVALID_TOOL_INPUT';

/** Status on the synthetic tool_result a captured (not yet executed) write tool call is answered with. */
export const PENDING_APPROVAL_STATUS = 'pending_approval';

export enum WriteToolName {
  RESOLVE_CLAIM = 'resolve_claim',
  SEND_MESSAGE = 'send_message',
  SEND_SLACK_MESSAGE = 'send_slack_message',
  WAIT = 'wait',
  RESET_CLAIM = 'reset_claim',
  UPDATE_CLAIM_TYPE = 'update_claim_type',
  UPDATE_SHIPPING_ADDRESS = 'update_shipping_address',
  VALIDATE_OFFLINE_CLAIM = 'validate_offline_claim',
  CREATE_DEPLOYMENT = 'create_deployment',
}

export const TOOL_ACTION_MAP: Record<string, { action: AutomationAction; aiDecision: AIDecision | null }> = {
  [WriteToolName.RESOLVE_CLAIM]: { action: AutomationAction.RESOLVE, aiDecision: null }, // set from input.decision
  [WriteToolName.SEND_MESSAGE]: { action: AutomationAction.SEND_MESSAGE, aiDecision: null },
  [WriteToolName.SEND_SLACK_MESSAGE]: { action: AutomationAction.ESCALATE, aiDecision: null },
  [WriteToolName.WAIT]: { action: AutomationAction.WAIT, aiDecision: null },
  [WriteToolName.UPDATE_CLAIM_TYPE]: { action: AutomationAction.UPDATE_CLAIM_TYPE, aiDecision: null },
  [WriteToolName.UPDATE_SHIPPING_ADDRESS]: { action: AutomationAction.UPDATE_SHIPPING_ADDRESS, aiDecision: null },
  [WriteToolName.VALIDATE_OFFLINE_CLAIM]: { action: AutomationAction.VALIDATE_OFFLINE, aiDecision: null },
};

/** Why an escalation could not be acted on: the agent lacked the means, or it needs a human's sign-off. */
export enum EscalationKind {
  CAPABILITY = 'CAPABILITY',
  JUDGEMENT = 'JUDGEMENT',
}

export enum EscalationTrigger {
  VALUE_GUARDRAIL = 'VALUE_GUARDRAIL',
  SOP_REQUIRES_APPROVAL = 'SOP_REQUIRES_APPROVAL',
  BRAND_OWNED = 'BRAND_OWNED',
  FRAUD_RISK = 'FRAUD_RISK',
  CUSTOMER_REQUESTED_HUMAN = 'CUSTOMER_REQUESTED_HUMAN',
  LEGAL_OR_SAFETY = 'LEGAL_OR_SAFETY',
  EVIDENCE_REVIEW = 'EVIDENCE_REVIEW',
  CUSTOMER_UNRESPONSIVE = 'CUSTOMER_UNRESPONSIVE',
  NO_ENABLED_RESOLUTION = 'NO_ENABLED_RESOLUTION',
  TOOL_OR_PLATFORM_LIMIT = 'TOOL_OR_PLATFORM_LIMIT',
  PRICING_FAILURE = 'PRICING_FAILURE',
  OTHER = 'OTHER',
}

/** The only kind each trigger can carry; OTHER (null) pairs with either. */
export const ESCALATION_TRIGGER_KIND: Record<EscalationTrigger, EscalationKind | null> = {
  [EscalationTrigger.VALUE_GUARDRAIL]: EscalationKind.JUDGEMENT,
  [EscalationTrigger.SOP_REQUIRES_APPROVAL]: EscalationKind.JUDGEMENT,
  [EscalationTrigger.BRAND_OWNED]: EscalationKind.JUDGEMENT,
  [EscalationTrigger.FRAUD_RISK]: EscalationKind.JUDGEMENT,
  [EscalationTrigger.CUSTOMER_REQUESTED_HUMAN]: EscalationKind.JUDGEMENT,
  [EscalationTrigger.LEGAL_OR_SAFETY]: EscalationKind.JUDGEMENT,
  [EscalationTrigger.EVIDENCE_REVIEW]: EscalationKind.JUDGEMENT,
  [EscalationTrigger.CUSTOMER_UNRESPONSIVE]: EscalationKind.JUDGEMENT,
  [EscalationTrigger.NO_ENABLED_RESOLUTION]: EscalationKind.CAPABILITY,
  [EscalationTrigger.TOOL_OR_PLATFORM_LIMIT]: EscalationKind.CAPABILITY,
  [EscalationTrigger.PRICING_FAILURE]: EscalationKind.CAPABILITY,
  [EscalationTrigger.OTHER]: null,
};

/** The triggers a store can let auto-approve; brand holds, fraud, legal/safety and asks for a human always need a person. */
export const AUTO_APPROVABLE_ESCALATION_TRIGGERS: readonly EscalationTrigger[] = [
  EscalationTrigger.VALUE_GUARDRAIL,
  EscalationTrigger.SOP_REQUIRES_APPROVAL,
  EscalationTrigger.EVIDENCE_REVIEW,
  EscalationTrigger.CUSTOMER_UNRESPONSIVE,
];

/** A store's auto-approve settings before it changes them; the merchant-svc column defaults match. */
export const DEFAULT_AUTO_APPROVE_MAX_AMOUNT = 1000;
/** The most a store's auto-approve limit can be, since nobody reviews the payout. */
export const AUTO_APPROVE_MAX_AMOUNT_CEILING = 10000;
export const DEFAULT_AUTO_APPROVE_TRIGGERS: readonly EscalationTrigger[] = [
  EscalationTrigger.EVIDENCE_REVIEW,
  EscalationTrigger.SOP_REQUIRES_APPROVAL,
  EscalationTrigger.CUSTOMER_UNRESPONSIVE,
];

/** Maps markdown display names → camelCase keys for scorecard parsing */
export const SCORECARD_KEY_ALIASES: Record<string, string> = {
  // Dimension weights
  'data readiness': 'dataReadiness',
  'decision clarity': 'decisionClarity',
  'llm confidence': 'llmConfidence',
  // Decision clarity penalties
  'no compliance report': 'noComplianceReport',
  'high iterations': 'highIterations',
  'very high iterations': 'veryHighIterations',
  'high iteration threshold': 'highIterationThreshold',
  'very high iteration threshold': 'veryHighIterationThreshold',
  // Guardrails
  'min llm confidence': 'minLlmConfidence',
  'require deny precedent': 'requireDenyPrecedent',
};

/** The AI features whose model an admin can pick on the AI Models page, with the name the picker shows. */
export const PICKABLE_AI_FEATURE_LABELS: Partial<Record<AiFeature, string>> = {
  [AiFeature.CLAIM_AUTOMATION]: 'Claim automation',
  [AiFeature.PRODUCT_FINDER]: 'Product finder',
  [AiFeature.RECEIPT_READER]: 'Receipt reader',
  [AiFeature.SLACK_BOT]: 'Slack bot',
  [AiFeature.THEME_AUTOMATION]: 'Theme automation',
  [AiFeature.METABASE_REPORT]: 'Metabase reports',
  [AiFeature.BRAND_INQUIRY]: 'Brand inquiry',
  [AiFeature.REACTION_LANE]: 'Reaction lane',
  [AiFeature.CLAIM_MESSAGE_TRIAGE]: 'Message triage summary',
  [AiFeature.SOP_GENERATION]: 'SOP generation',
  [AiFeature.AUTOMATION_TRAINING]: 'Automation training',
  [AiFeature.POD_SCAN]: 'Proof of delivery scan',
  [AiFeature.PRODUCT_INDEX]: 'Product index (embeddings)',
  [AiFeature.MEDIA_ANALYSIS]: 'Media analysis',
  [AiFeature.NOTE_ACTION_GATE]: 'Note action gate',
  [AiFeature.MCP_DECIDE]: 'MCP decide',
  [AiFeature.AI_DECISION_AUDIT]: 'AI decision audit',
};

export const PICKABLE_AI_FEATURES = Object.keys(PICKABLE_AI_FEATURE_LABELS) as AiFeature[];

/** The AI features with no model picker, and why, so the AI Models page can show them disabled. */
export const UNPICKABLE_AI_FEATURES: Partial<Record<AiFeature, { label: string; reason: string }>> = {
  [AiFeature.FRAUD_SIGNALS]: {
    label: 'Fraud signals',
    reason: 'Not an AI model: a BigQuery lookup, listed only so its cost shows in AI Spend.',
  },
};

/** The features that send images, so only a model that reads images can be picked for them. */
export const IMAGE_INPUT_AI_FEATURES: readonly AiFeature[] = [
  AiFeature.RECEIPT_READER,
  AiFeature.POD_SCAN,
  AiFeature.MEDIA_ANALYSIS,
];

/** The Jev models TypeSafe's decide API serves, by company/model id; it has no catalog to list them from. */
export const JEV_DECISION_MODELS: readonly string[] = ['typesafe/jev-1.13.0'];

/** The Gemini models the Gemini SDK serves directly and that read images, by company/model id. */
export const GEMINI_MEDIA_MODELS: readonly string[] = ['google/gemini-2.5-pro', 'google/gemini-2.5-flash'];

/** The features whose client calls one vendor's API directly, so only these models can be picked for them. */
export const AI_FEATURE_ALLOWED_MODELS: Partial<Record<AiFeature, readonly string[]>> = {
  [AiFeature.MEDIA_ANALYSIS]: GEMINI_MEDIA_MODELS,
  [AiFeature.NOTE_ACTION_GATE]: JEV_DECISION_MODELS,
  [AiFeature.MCP_DECIDE]: JEV_DECISION_MODELS,
  [AiFeature.AI_DECISION_AUDIT]: JEV_DECISION_MODELS,
};

/** The features that embed text, so only an embedding model can be picked for them. */
export const EMBEDDING_AI_FEATURES: readonly AiFeature[] = [AiFeature.PRODUCT_INDEX];

/** A fallback by the id it runs as, alone or pinned to one OpenRouter provider, e.g. digitalocean. */
export type FeatureFallback = string | { model: string; provider: string };

/**
 * What a feature's call runs: its picked model, one of ours by the id we call it by, any other by its gateway id; and
 * the fallbacks its gateway calls try in order.
 */
export type FeatureModelInputs = { model?: string; gatewayModel?: string; fallbacks?: FeatureFallback[] };

/** The features whose model is picked per store; the AI Models page sets the default a store with no model of its own runs. */
export const PER_STORE_AI_FEATURES: readonly AiFeature[] = [AiFeature.CLAIM_AUTOMATION];

/** The most fallbacks a feature's chain holds after its model; OpenRouter takes at most 3 models a call. */
export const MAX_FALLBACKS = 2;

/** The features whose gateway calls can fall back: any pickable one but those limited to some models. */
export function takesFallbacks(feature: string): boolean {
  const known = feature as AiFeature;

  return (
    PICKABLE_AI_FEATURES.includes(known) && !EMBEDDING_AI_FEATURES.includes(known) && !AI_FEATURE_ALLOWED_MODELS[known]
  );
}
